import React, { Component, ErrorInfo, ReactNode } from 'react';
import LogRocketService from 'services/logRocketService';
import SentryService from 'services/sentryService';
import './ErrorBoundary.module.scss';

/**
 * ErrorBoundary Props
 */
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * ErrorBoundary State
 */
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree and
 * displays a fallback UI. Integrates with LogRocket to capture errors
 * with full context for debugging.
 *
 * Features:
 * - Catches React errors in child components
 * - Logs errors to LogRocket with context
 * - Displays user-friendly error UI
 * - Includes session URL for support
 * - Prevents entire app crash
 *
 * Usage:
 * ```tsx
 * // Wrap your entire app
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * // Or wrap specific sections
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <ComplexComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  /**
   * Log error to LogRocket and console
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const logRocketService = LogRocketService.getInstance();
    const sentryService = SentryService.getInstance();
    const context = {
      component: 'ErrorBoundary',
      errorInfo: errorInfo.componentStack,
      errorType: 'REACT_ERROR_BOUNDARY',
      timestamp: new Date().toISOString(),
    };

    if (logRocketService.isActive) {
      logRocketService.captureError(error, context);
      console.log('❌ Error captured by ErrorBoundary and sent to LogRocket');
      console.log('🔗 Session URL:', logRocketService.getSessionURL());
    }

    if (sentryService.isActive) {
      sentryService.captureError(error, context);
      console.log('❌ Error captured by ErrorBoundary and sent to Sentry');
    }

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });
  }

  /**
   * Reset error state (optional - for "try again" functionality)
   */
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Copy session URL to clipboard
   */
  copySessionURL = () => {
    const logRocketService = LogRocketService.getInstance();
    const sessionURL = logRocketService.getSessionURL();

    if (sessionURL) {
      navigator.clipboard.writeText(sessionURL);
      alert('Session URL copied to clipboard!');
    } else {
      alert('Session URL not available');
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const logRocketService = LogRocketService.getInstance();
      const sessionURL = logRocketService.getSessionURL();

      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__icon">⚠️</div>
            <h1 className="error-boundary__title">
              Something went wrong
            </h1>
            <p className="error-boundary__message">
              We're sorry for the inconvenience. The error has been logged
              and our team has been notified.
            </p>

            {this.state.error && (
              <details className="error-boundary__details">
                <summary>Error Details</summary>
                <pre className="error-boundary__error-message">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <pre className="error-boundary__stack-trace">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            <div className="error-boundary__actions">
              <button
                className="error-boundary__button error-boundary__button--primary"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>

              {sessionURL && (
                <button
                  className="error-boundary__button error-boundary__button--secondary"
                  onClick={this.copySessionURL}
                >
                  Copy Session URL for Support
                </button>
              )}
            </div>

            {sessionURL && (
              <p className="error-boundary__support-info">
                Need help? Include the session URL when contacting support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

